import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Briefcase,
  FileText,
  AlertTriangle,
  Info,
  Hash,
  Palette,
  Tag
} from "lucide-react";
import { Asset } from "../../../types/asset";
import { ProjectType } from "../../../types/projectType";
import api from "../../../services/axiosInstance";
import Select from "../../../shared/components/form/Select";
import Button from "../../../shared/components/ui/button/Button";

// Modern Clean Brand Colors - Subtle & Refined
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  accent: "#A3CD39",           // Lime accent
  dark: "#007F66",             // Deep teal
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

interface Props {
  onAdd: (asset: Asset) => void;
  onClose?: () => void;
  editingAsset?: Asset | null;
  projectTypes: ProjectType[];
  /** When creating a new asset, pre-select this project type. */
  defaultProjectTypeId?: string;
}

const schema = yup.object({
  name: yup.string()
    .required("Asset name is required")
    .min(2, "Asset name must be at least 2 characters")
    .max(100, "Asset name must be less than 100 characters"),

  projectTypeId: yup.string().required("Project type is required"),

  code: yup.string()
    .optional()
    .matches(/^[A-Z0-9-]+$/, "Code can only contain letters, numbers, and hyphens")
    .min(3, "Code must be at least 3 characters")
    .max(15, "Code cannot exceed 15 characters"),

  // Architecture Rules
  category: yup.string()
    .optional()
    .oneOf(['generation', 'collection', 'conversion', 'transmission', 'connection', 'protection', 'structure', 'monitoring', 'safety', 'security', 'other'], 'Invalid category'),

  color: yup.string()
    .optional()
    .matches(/^#[0-9A-Fa-f]{6}$/, "Color must be a valid hex code (e.g., #0369A1)"),
});

type FormValues = yup.InferType<typeof schema>;

export default function AssetForm({ onAdd, onClose, editingAsset, projectTypes, defaultProjectTypeId }: Props) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [suggestedCode, setSuggestedCode] = useState<string | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [detectedModuleType, setDetectedModuleType] = useState<string | null>(null);
  const [codeDescription, setCodeDescription] = useState<string | null>(null);
  const [suggestedCategory, setSuggestedCategory] = useState<string>('');
  const [suggestedColor, setSuggestedColor] = useState<string>('');

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty, isValid },
    setFocus
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange"
  });

  const watchedName = watch("name");
  const watchedCategory = watch("category");
  const watchedColor = watch("color");

  // Generate project type options
  const projectTypeOptions = projectTypes.map((projectType) => ({
    value: projectType._id,
    label: projectType.name,
  }));

  // Function to generate suggested code based on asset name
  const generateSuggestedCode = async (assetName: string) => {
    if (!assetName.trim()) {
      setSuggestedCode(null);
      setDetectedModuleType(null);
      setCodeDescription(null);
      return;
    }

    setIsGeneratingCode(true);
    try {
      const response = await api.post('/assets/suggest-code', { name: assetName });
      const { code, moduleType, description } = response.data.data;
      setSuggestedCode(code);
      setDetectedModuleType(moduleType);
      setCodeDescription(description);
    } catch (error) {
      console.error('Failed to generate suggested code:', error);
      setSuggestedCode(null);
      setDetectedModuleType(null);
      setCodeDescription(null);
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Function to detect category and color from asset name
  const detectCategoryAndColor = (assetName: string) => {
    if (!assetName.trim()) {
      setSuggestedCategory('');
      setSuggestedColor('');
      return;
    }

    const nameLower = assetName.toLowerCase();
    let category = 'other';
    let color = '#6B7280'; // Default gray

    if (nameLower.includes('module') || nameLower.includes('panel') || nameLower.includes('pv')) {
      category = 'generation';
      color = '#0369A1'; // Blue
    } else if (nameLower.includes('string') || nameLower.includes('array')) {
      category = 'collection';
      color = '#8B5CF6'; // Purple
    } else if (nameLower.includes('inverter')) {
      category = 'conversion';
      color = '#EF4444'; // Red
    } else if (nameLower.includes('transformer')) {
      category = 'transmission';
      color = '#F59E0B'; // Orange
    } else if (nameLower.includes('combiner') || nameLower.includes('junction')) {
      category = 'collection';
      color = '#10B981'; // Green
    } else if (nameLower.includes('cable') || nameLower.includes('wire')) {
      category = 'connection';
      color = nameLower.includes('ac') ? '#EC4899' : '#6B7280'; // Pink for AC, Gray for DC
    } else if (nameLower.includes('grid') || nameLower.includes('connection')) {
      category = 'other';
      color = '#8B5CF6'; // Purple
    }

    setSuggestedCategory(category);
    setSuggestedColor(color);

    // Auto-fill if fields are empty (not editing)
    if (!editingAsset && !watchedCategory) {
      setValue('category', category, { shouldValidate: true });
    }
    if (!editingAsset && !watchedColor) {
      setValue('color', color, { shouldValidate: true });
    }
  };

  // Generate suggested code and detect category/color when asset name changes (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (watchedName && watchedName.trim()) {
        generateSuggestedCode(watchedName);
        detectCategoryAndColor(watchedName);
      } else {
        setSuggestedCode(null);
        setDetectedModuleType(null);
        setCodeDescription(null);
        setSuggestedCategory('');
        setSuggestedColor('');
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [watchedName]);

  // Log project types for debugging
  useEffect(() => {
    console.log('AssetForm - Project Types loaded:', projectTypes.length, projectTypes);
  }, [projectTypes]);

  // Auto-fill form if editing
  useEffect(() => {
    if (editingAsset) {
      const projectTypeIdValue = typeof editingAsset.projectTypeId === "string"
        ? editingAsset.projectTypeId
        : editingAsset.projectTypeId?._id || "";

      reset({
        name: editingAsset.name,
        projectTypeId: projectTypeIdValue,
        code: editingAsset.code || '',
        category: (editingAsset as any).architectureRules?.category || '',
        color: (editingAsset as any).architectureRules?.color || '',
      });
    } else {
      reset({ projectTypeId: defaultProjectTypeId || '' } as Partial<FormValues>);
    }
    setFocus("name");
  }, [editingAsset, reset, setFocus, defaultProjectTypeId]);

  const onSubmit = async (data: FormValues) => {
    setApiError(null);
    setShowSuccess(false);

    try {
      // Prepare payload with architectureRules
      const payload: any = {
        name: data.name,
        projectTypeId: data.projectTypeId,
        code: data.code,
      };

      // Add architectureRules if category or color is provided
      if (data.category || data.color) {
        payload.architectureRules = {
          ...(data.category && { category: data.category }),
          ...(data.color && { color: data.color }),
        };
      }

      const response = editingAsset
        ? await api.patch<{ data: Asset }>(`/assets/${editingAsset.id}`, payload)
        : await api.post<{ data: Asset }>("/assets", payload);

      setShowSuccess(true);

      toast.success(
        editingAsset
          ? "Asset updated successfully!"
          : "Asset created successfully!"
      );

      setTimeout(() => {
        const assetData = (response.data as any).data || response.data;
        onAdd(assetData);
        onClose?.();
        reset();
      }, 1000);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      const message =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${editingAsset ? "update" : "create"} asset`;

      setApiError(message);
      toast.error(message);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="w-full max-w-2xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
            <Settings className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingAsset ? "Edit Asset" : "Add New Asset"}
            </h2>
            <p className="text-sm text-gray-500">
              {editingAsset ? "Update asset information" : "Create a new asset entry"}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Success! Asset {editingAsset ? "updated" : "created"} successfully.
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Redirecting...
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      <AnimatePresence>
        {apiError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600 mt-1">{apiError}</p>
              </div>
              <button
                onClick={() => setApiError(null)}
                className="ml-auto p-1 text-red-400 hover:text-red-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">


        {/* Project Type */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <Briefcase className="w-4 h-4 inline mr-2" />
            Project Type *
          </label>
          <Controller
            name="projectTypeId"
            control={control}
            render={({ field }) => (
              <div className="relative">
                <Select
                  {...field}
                  options={projectTypeOptions}
                  placeholder="Select project type"
                  value={projectTypeOptions.find(opt => opt.value === field.value)}
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption?.value || "");
                  }}
                  className={errors.projectTypeId ? "border-red-300" : ""}
                />
              </div>
            )}
          />
          {errors.projectTypeId && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-600 flex items-center gap-1"
            >
              <AlertTriangle className="w-3 h-3" />
              {errors.projectTypeId.message}
            </motion.p>
          )}
          <p className="text-xs text-gray-500">
            Choose the project type this asset belongs to
          </p>
        </div>

        {/* Asset Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <FileText className="w-4 h-4 inline mr-2" />
            Asset Name *
          </label>
          <div className="relative">
            <input
              {...register("name")}
              type="text"
              className={`block w-full px-4 py-3 border rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${errors.name
                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-200 bg-gray-50 focus:bg-white focus:border-2"
                }`}
              style={!errors.name ? { '--tw-border-color': BRAND_COLORS.primary } as React.CSSProperties : {}}
              placeholder="Enter asset name (e.g., PV Modules, Wind Turbine)"
            />
            {watchedName && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
            )}
          </div>
          {errors.name && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-600 flex items-center gap-1"
            >
              <AlertTriangle className="w-3 h-3" />
              {errors.name.message}
            </motion.p>
          )}
          <p className="text-xs text-gray-500">
            Enter the specific type or name of the asset
          </p>
        </div>

        {/* Category Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <Tag className="w-4 h-4 inline mr-2" />
            Asset Category
          </label>

          {/* Suggested Category Display */}
          {suggestedCategory && suggestedCategory !== watchedCategory && !editingAsset && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs font-medium text-blue-800 mb-1">Suggested Category</p>
                  <p className="text-sm text-blue-700 capitalize">{suggestedCategory}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setValue("category", suggestedCategory, { shouldValidate: true, shouldDirty: true });
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Use This
                </button>
              </div>
            </div>
          )}

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <select
                {...field}
                className={`block w-full px-4 py-3 border rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${errors.category
                  ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-200 bg-gray-50 focus:bg-white focus:border-2"
                  }`}
                style={!errors.category ? { '--tw-border-color': BRAND_COLORS.primary } as React.CSSProperties : {}}
              >
                <option value="">Select category (optional)</option>
                <option value="generation">Generation</option>
                <option value="collection">Collection</option>
                <option value="conversion">Conversion</option>
                <option value="transmission">Transmission</option>
                <option value="connection">Connection</option>
                <option value="protection">Protection</option>
                <option value="structure">Structure</option>
                <option value="monitoring">Monitoring</option>
                <option value="safety">Safety</option>
                <option value="security">Security</option>
                <option value="other">Other</option>
              </select>
            )}
          />
          {errors.category && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-600 flex items-center gap-1"
            >
              <AlertTriangle className="w-3 h-3" />
              {errors.category.message}
            </motion.p>
          )}
          <p className="text-xs text-gray-500">
            Category helps organize assets in the architecture builder
          </p>
        </div>

        {/* Color Picker */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <Palette className="w-4 h-4 inline mr-2" />
            Display Color
          </label>

          {/* Suggested Color Display */}
          {suggestedColor && suggestedColor !== watchedColor && !editingAsset && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded border-2 border-white shadow-sm"
                    style={{ backgroundColor: suggestedColor }}
                  />
                  <div>
                    <p className="text-xs font-medium text-blue-800 mb-1">Suggested Color</p>
                    <p className="text-sm text-blue-700 font-mono">{suggestedColor}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setValue("color", suggestedColor, { shouldValidate: true, shouldDirty: true });
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Use This
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <>
                  <input
                    {...field}
                    type="color"
                    className="h-12 w-20 rounded-lg border-2 border-gray-200 cursor-pointer"
                  />
                  <input
                    {...field}
                    type="text"
                    placeholder="#0369A1"
                    className={`flex-1 px-4 py-3 border rounded-lg text-sm font-mono transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${errors.color
                      ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 bg-gray-50 focus:bg-white focus:border-2"
                      }`}
                    style={!errors.color ? { '--tw-border-color': BRAND_COLORS.primary } as React.CSSProperties : {}}
                  />
                </>
              )}
            />
          </div>
          {errors.color && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-red-600 flex items-center gap-1"
            >
              <AlertTriangle className="w-3 h-3" />
              {errors.color.message}
            </motion.p>
          )}
          <p className="text-xs text-gray-500">
            Color will be used to display this asset in the architecture builder
          </p>
        </div>

        {/* Asset Code */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <Hash className="w-4 h-4 inline mr-2" />
            Asset Code
          </label>
          <div className="space-y-2">
            {/* Suggested Code Display */}
            {suggestedCode && !editingAsset && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800 mb-1">Suggested Code</p>
                    <p className="text-sm text-blue-700 font-medium mb-1">
                      {codeDescription || 'Based on asset name'}
                    </p>
                    <p className="text-xs text-blue-600">
                      Detected from: "{watchedName}"
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <code className="px-3 py-2 bg-blue-100 text-blue-800 rounded text-lg font-mono font-bold">
                      {suggestedCode}
                    </code>
                    <button
                      type="button"
                      onClick={() => {
                        setValue("code", suggestedCode, { shouldValidate: true, shouldDirty: true });
                        trigger("code");
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                    >
                      Use This Code
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Code Input */}
            <div className="relative">
              <input
                {...register("code")}
                type="text"
                className={`block w-full px-4 py-3 border rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${errors.code
                  ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-200 bg-gray-50 focus:bg-white focus:border-2"
                  }`}
                style={!errors.code ? { '--tw-border-color': BRAND_COLORS.primary } as React.CSSProperties : {}}
                placeholder="Enter asset code (e.g., INVSOL, MODPAN, SCBCOM)"
              />
              {isGeneratingCode && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                </div>
              )}
            </div>

            {errors.code && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-red-600 flex items-center gap-1"
              >
                <AlertTriangle className="w-3 h-3" />
                {errors.code.message}
              </motion.p>
            )}
            <p className="text-xs text-gray-500">
              {editingAsset ? 'Edit the asset code' : 'Code will be suggested based on asset name, or enter custom code'}
            </p>
          </div>
        </div>
        {/* Additional Info Box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Asset Information</p>
              <p className="text-xs text-blue-600 mt-1">
                Assets will be automatically set to "active" status upon creation. You can manage status using the toggle in the assets table.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col-reverse sm:flex-row gap-3 pt-5 pb-2 border-t border-gray-200 bg-white z-10">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200"
              style={{ '--tw-ring-color': '#9CA3AF' } as React.CSSProperties}
            >
              Cancel
            </button>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !isValid}
            className="flex-1 sm:flex-none px-6 py-3 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: BRAND_COLORS.primary,
              '--tw-ring-color': BRAND_COLORS.primary
            } as React.CSSProperties}
            onMouseEnter={(e) => {
              if (!isSubmitting && isValid) {
                e.currentTarget.style.backgroundColor = BRAND_COLORS.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && isValid) {
                e.currentTarget.style.backgroundColor = BRAND_COLORS.primary;
              }
            }}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingAsset ? "Updating..." : "Creating..."}
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Settings className="w-4 h-4" />
                {editingAsset ? "Update Asset" : "Create Asset"}
              </div>
            )}
          </button>
        </div>

        {/* Form Status Indicator */}
        <div className="flex items-center justify-between text-xs text-gray-500 pt-2">
          <div className="flex items-center gap-4">
            <span className={`flex items-center gap-1 ${Object.keys(errors).length === 0 ? "text-green-600" : "text-red-600"
              }`}>
              {Object.keys(errors).length === 0 ? (
                <CheckCircle2 className="w-3 h-3" />
              ) : (
                <AlertTriangle className="w-3 h-3" />
              )}
              {Object.keys(errors).length === 0 ? "Form valid" : `${Object.keys(errors).length} error(s)`}
            </span>
            {isDirty && (
              <span className="text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Unsaved changes
              </span>
            )}
          </div>
          <span>
            Fields marked with * are required
          </span>
        </div>
      </form>
    </motion.div>
  );
} 